import { CopyLine } from '@sbercloud/uikit-product-copy-line';
import { AccordionSecondary } from '@snack-uikit/accordion';
import { Card } from '@snack-uikit/card';
import { Typography } from '@snack-uikit/typography';
import { MathJax } from 'better-react-mathjax';
import cn from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { ReactElement } from 'react';

import { DYNAMIC_CONTENT_CLASS_NAMES, geAccordionClassname } from '@/constant/dynamic';
import { DATA_TEST_IDS_INJECTORS } from '@/constant/tests';
import { getGlobalArgs } from '@/root/global';
import { FindElementPredicate } from '@/types/ssr';
import { complement, either, equals, head } from '@/utils/common';
import { resolvePath, skipFileExtension, toAbsolutePath } from '@/utils/navigation';
import { removeEmptyStrings } from '@/utils/strings';

import { CardsGrid } from '../../static';
import { Injector } from '../types';
import { classNameIs, collectTextContent, curriedElementPropEq, findElements, isDtElement } from '../utils';
import s from './styles.module.scss';
import { getNodeClass, isHttpNodeClass, makeInjector, nodeClassIncludes, nodeClassIs, nodeNameIs } from './utils';

export const ddInjector = makeInjector(
  (_, children) => (
    <div data-test-id={DATA_TEST_IDS_INJECTORS.dd} className={s.dd_wrapper}>
      {children}
    </div>
  ),
  nodeNameIs('dd'),
);

export const sectionInjector: Injector = makeInjector(
  (node, children) => (
    <section id={node.attribs.id} data-test-id={DATA_TEST_IDS_INJECTORS.section} className={s.section_wrapper}>
      {children}
    </section>
  ),
  nodeClassIs(DYNAMIC_CONTENT_CLASS_NAMES.section),
);

export const olArabicInjector = makeInjector(
  (node, children) => (
    <ol
      start={Number(node.attribs.start || 1)}
      data-test-id={DATA_TEST_IDS_INJECTORS.ol}
      className={cn(s.ol_wrapper, { [s.lower_alpha]: getNodeClass(node).includes('loweralpha') })}
    >
      {children}
    </ol>
  ),
  either(nodeClassIs(DATA_TEST_IDS_INJECTORS.ol), nodeNameIs('ol')),
);

export const ulInjector = makeInjector(
  (_, children) => (
    <ul data-test-id={DATA_TEST_IDS_INJECTORS.ul} className={s.ul_wrapper}>
      {children}
    </ul>
  ),
  nodeNameIs('ul'),
);

export const liInjector = makeInjector(
  (_, children) => (
    <li data-test-id={DATA_TEST_IDS_INJECTORS.li} className={s.li_wrapper}>
      {children}
    </li>
  ),
  nodeNameIs('li'),
);

export const imgToNextImageInjector = makeInjector(node => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const pathname = usePathname();
  const resolvedPath = resolvePath(toAbsolutePath(pathname), node.attribs.src);

  return (
    <img
      data-test-id={DATA_TEST_IDS_INJECTORS.img}
      alt={node.attribs.alt}
      src={`${getGlobalArgs('NEXT_PUBLIC_IMAGES_PROXY_URL', '')}${resolvedPath}`}
      className={s.image_wrapper}
    />
  );
}, nodeNameIs('img'));

export const accordionInjector = makeInjector((_, children) => {
  const accordionHeaders = findElements(children, classNameIs(geAccordionClassname('header')));
  const accordionContent = findElements(children, classNameIs(geAccordionClassname('content')));

  const header = collectTextContent(accordionHeaders).join(' ');

  return (
    <AccordionSecondary data-test-id={DATA_TEST_IDS_INJECTORS.accordion} className={s.accordion_wrapper}>
      <AccordionSecondary.CollapseBlock
        id={header}
        className={s.accordion_content_root}
        header={
          <div className={s.accordion_header}>
            <Typography.SansBodyL> {header} </Typography.SansBodyL>
          </div>
        }
      >
        <div className={s.accordion_content_wrapper}>{accordionContent}</div>
      </AccordionSecondary.CollapseBlock>
    </AccordionSecondary>
  );
}, nodeClassIncludes(DYNAMIC_CONTENT_CLASS_NAMES.toggleGroup));

export const captionInjector = makeInjector(
  (_, children) => (
    <div data-test-id={DATA_TEST_IDS_INJECTORS.caption} className={s.caption_wrapper}>
      <Typography.SansBodyM>{collectTextContent(children)}</Typography.SansBodyM>
    </div>
  ),
  nodeClassIs(DYNAMIC_CONTENT_CLASS_NAMES.codeBlockCaption),
);

export const dlHttpInjector = makeInjector((_, children) => {
  const isDtElementByTestId: FindElementPredicate = element => {
    if (!element?.props) return false;
    return equals(element.props['data-test-id'], DATA_TEST_IDS_INJECTORS.dt);
  };

  const isDt = either(isDtElementByTestId, isDtElement);
  const endpointElements = findElements(children, isDt);

  const isEqualWithEndpointElement = equals(head(endpointElements));

  return (
    <div data-test-id={DATA_TEST_IDS_INJECTORS.dlHttp}>
      <CopyLine
        className={s.http_endpoint_element_wrapper}
        content={
          <Typography.SansTitleM>
            {removeEmptyStrings(collectTextContent(endpointElements)).slice(0, 2).join(' ')}
          </Typography.SansTitleM>
        }
      />

      {children.filter(complement(isEqualWithEndpointElement))}
    </div>
  );
}, isHttpNodeClass);

export const dlListInjector = makeInjector(
  (_, children) => (
    <div data-test-id={DATA_TEST_IDS_INJECTORS.dl} className={s.dl_field_list_wrapper}>
      {children}
    </div>
  ),
  nodeClassIs(DYNAMIC_CONTENT_CLASS_NAMES.fieldList),
);

export const mathInjector = makeInjector(
  (_, children) => (
    <MathJax className={s.scroll_offset} inline dynamic>
      {children}
    </MathJax>
  ),
  nodeClassIncludes(DYNAMIC_CONTENT_CLASS_NAMES.math),
);

export const serviceCardsGridInjector = makeInjector(
  (_, children) => <CardsGrid>{children}</CardsGrid>,
  nodeClassIs(DYNAMIC_CONTENT_CLASS_NAMES.serviceCardsWrapper),
);

export const serviceCardInjector = makeInjector((_, children) => {
  const getLinkData = (el?: ReactElement): { isExternal: boolean; link: string } => {
    if (!el) return { isExternal: false, link: '' };
    if ('href' in el.props) return { isExternal: true, link: el.props.href };
    return { isExternal: false, link: el.props.link };
  };

  const isLinkDataTestId = curriedElementPropEq('data-test-id')(DATA_TEST_IDS_INJECTORS.link);
  const headers = findElements(children, classNameIs(DYNAMIC_CONTENT_CLASS_NAMES.serviceCardTitle));
  const descriptions = findElements(children, classNameIs(DYNAMIC_CONTENT_CLASS_NAMES.serviceCardDescription));
  const links = findElements(headers, isLinkDataTestId);

  const { isExternal, link } = getLinkData(head(links));
  const href = isExternal ? link : skipFileExtension('html', link);
  return (
    <Link className={s.service_card} target={isExternal ? '_blank' : '_self'} href={href}>
      <Card
        className={s.full_size}
        header={
          <Typography.SansTitleM className={s.service_content_wrapper}>
            {removeEmptyStrings(collectTextContent(headers))}
          </Typography.SansTitleM>
        }
        outline
      >
        <div className={s.service_content_wrapper}>{descriptions}</div>
      </Card>
    </Link>
  );
}, nodeClassIs(DYNAMIC_CONTENT_CLASS_NAMES.serviceCard));
